/**
 * How-to-use guide — modal with step-by-step instructions and tips.
 */
import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { HelpCircle, X, Calculator as CalcIcon, Users, ShieldAlert, Wallet, Gauge, Landmark, Package, Coins, TrendingUp, FileText, MessageSquare } from 'lucide-react';

const STEPS = [
  { icon: <CalcIcon className="h-4 w-4" />, title: 'Calculator', body: 'Pick your sector / sub-sector, region (and optionally city), contract type, criticality and project size. Adjust shift pattern, BIM LOD and procurement model to fine-tune the cost. Click Calculate to generate the estimate.' },
  { icon: <Users className="h-4 w-4" />, title: 'Resources', body: 'Auto-derived crew, materials and equipment plan from the calculation. Edit quantities and rates inline — totals update live.' },
  { icon: <ShieldAlert className="h-4 w-4" />, title: 'Risk & Change', body: 'Library of common risks pre-filtered for your sector and city. Add risks, set probability × impact, log change orders. Expected risk cost flows into Funding and KPIs automatically.' },
  { icon: <Wallet className="h-4 w-4" />, title: 'Funding', body: 'Choose a burn curve (S-curve, linear, front/back-loaded), enter monthly funding inflows and per-month notes. The chart shows shortfall months in red.' },
  { icon: <TrendingUp className="h-4 w-4" />, title: 'EVM', body: 'Track Planned Value, Earned Value and Actual Cost over time. CPI/SPI, EAC/ETC/VAC and TCPI are computed automatically.' },
  { icon: <Gauge className="h-4 w-4" />, title: 'KPIs', body: 'Define Red/Amber/Green thresholds per KPI with your own operators (<, ≤, >, ≥). Settings persist locally.' },
  { icon: <Landmark className="h-4 w-4" />, title: 'Tax & Policies', body: 'Indicative regional tax, social charges, contingency and policy notes. Review with a local advisor before tendering.' },
  { icon: <FileText className="h-4 w-4" />, title: 'Dashboard', body: 'Consolidated visual view across all tabs — pick the tab that best tells the story to your sponsor.' },
  { icon: <MessageSquare className="h-4 w-4" />, title: 'Collaboration', body: 'Invite team members by email per saved project, leave comments on budget lines, record approvals; everything is captured in an audit trail.' },
  { icon: <Package className="h-4 w-4" />, title: 'Rate Sheets', body: 'Browse seeded material & labour rates with a regional cost multiplier. Use Refresh live rates (AI) for an indicative location-specific snapshot.' },
  { icon: <Coins className="h-4 w-4" />, title: 'Forex', body: 'Edit indicative USD-to-target currency rates and persist them.' },
];

export default function HowToGuide() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="how-to-guide-btn" className="no-print">
        <HelpCircle className="h-3.5 w-3.5 mr-1.5" />
        How to use
      </Button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 no-print" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="how-to-guide-modal">
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5 text-orange-600" />
                <h2 className="text-lg font-semibold">How to use this estimator</h2>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-slate-600">Work through the tabs left-to-right. Each tab unlocks once a calculation is run.</p>
              {STEPS.map((s, i) => (
                <div key={s.title} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-center w-8 h-8 rounded-md bg-slate-900 text-white shrink-0">{s.icon}</div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{i + 1}. {s.title}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{s.body}</div>
                  </div>
                </div>
              ))}
              <div className="text-xs text-slate-500 mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <strong>Tip:</strong> hover the small <span className="inline-block align-middle">ⓘ</span> icon next to any field for a quick definition.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
